import { useCallback, useState } from 'react'
import {
  useAddGuestIdentityMutation,
  IdentityType
} from 'src/apollo/onlineOrdering'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import { alertError } from 'src/shared/js/alertUtils'
import { showEmailUpdateError } from '../utils/accountNotifications'

export const useEmailVerification = () => {
  const ooClient = useOOClient()
  const [addGuestIdentity] = useAddGuestIdentityMutation()
  const [updatedEmail, setUpdatedEmail] = useState('')

  const sendVerificationCode = useCallback(
    async (email: string) => {
      if (email === '')
        return {
          data: {
            addGuestIdentity: {
              __typename: 'InvalidEmailError',
              message:
                'There was an error sending the verification code. Please try again.'
            }
          }
        }
      return await addGuestIdentity({
        variables: {
          input: {
            identityType: IdentityType.Email,
            identityString: email
          }
        },
        client: ooClient
      })
    },
    [addGuestIdentity, ooClient]
  )

  const initiateEmailUpdate = useCallback(
    async (email: string | null | undefined): Promise<boolean> => {
      if (!email) return false

      try {
        const { data } = await sendVerificationCode(email)
        const response = data?.addGuestIdentity

        switch (response?.__typename) {
          case 'AddGuestIdentitySuccess':
            setUpdatedEmail(email)
            return true
          case 'GuestIdentityAlreadyExistsForCurrentGuestError':
          case 'InvalidEmailError':
          case 'ProfileLockedForDeletionError':
          case 'UnexpectedError':
            alertError(response.message)
            return false
          default:
            return false
        }
      } catch (err) {
        showEmailUpdateError()
        return false
      }
    },
    [sendVerificationCode]
  )

  const clearUpdatedEmail = useCallback(() => {
    setUpdatedEmail('')
  }, [])

  return {
    updatedEmail,
    sendVerificationCode,
    initiateEmailUpdate,
    clearUpdatedEmail
  }
}
